import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useRef, useState } from 'react';
import { Mail, Github, Linkedin, Send, Phone, MapPin } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';

export function ContactPage() {
    const containerRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLDivElement>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    // Signal path animation
    const pathLength = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
    const signalOpacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 1, 1, 0]);

    // Connect intensity - as we get closer to the form, the intensity increases
    const glowScale = useTransform(scrollYProgress, [0.7, 1], [1, 2]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitted(true);
        setTimeout(() => setIsSubmitted(false), 5000);
    };

    return (
        <div ref={containerRef} className="bg-tech-bg min-h-[200vh] relative">
            {/* The Signal Line - Follows scroll */}
            <div className="fixed top-0 left-1/2 -translate-x-1/2 h-full w-0.5 z-0 pointer-events-none">
                <div className="h-full w-full bg-emerald-500/10" />
                <motion.div
                    className="absolute top-0 left-0 w-full bg-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.8)]"
                    style={{ height: '100%', scaleY: pathLength, originY: 0, opacity: signalOpacity }}
                />
                <motion.div
                    className="absolute w-4 h-4 -left-[7px] bg-emerald-400 rounded-full blur-[4px]"
                    style={{ top: `${pathLength.get() * 100}%`, display: scrollYProgress.get() > 0 ? 'block' : 'none' }}
                />
            </div>

            {/* Header Section */}
            <section className="h-screen flex flex-col items-center justify-center text-center px-4 relative z-10 overflow-hidden">
                <div className="absolute inset-0 z-0 opacity-40">
                    <img
                        src="/cinematic-handshake-signal-transmission-secure-protocol.png"
                        alt="Contact Handshake Signal"
                        className="w-full h-full object-cover blur-xl"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-tech-bg/80 via-transparent to-tech-bg" />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    className="space-y-6 relative z-10"
                >
                    <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter">
                        SEND A <span className="text-emerald-500">SIGNAL</span>
                    </h1>
                    <p className="text-xl text-gray-400 font-mono tracking-widest uppercase">
                        Establishing secure handshake protocol...
                    </p>
                    <div className="mt-20">
                        <motion.div
                            animate={{ y: [0, 20, 0], opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="flex flex-col items-center gap-4"
                        >
                            <span className="text-xs text-emerald-500/50 uppercase tracking-[0.4em]">Scroll to Connect</span>
                            <div className="w-px h-24 bg-gradient-to-b from-emerald-500 to-transparent" />
                        </motion.div>
                    </div>
                </motion.div>
            </section>

            {/* Form Section */}
            <section className="min-h-screen flex items-center justify-center py-32 px-6 relative z-10">
                <motion.div
                    ref={formRef}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ margin: "-100px" }}
                    className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-2 gap-12"
                >
                    {/* Contact Info */}
                    <div className="flex flex-col justify-center space-y-12">
                        <div className="space-y-4">
                            <h2 className="text-4xl font-bold text-white">UPHOLDING THE <span className="text-emerald-500">CONNECTION</span></h2>
                            <p className="text-gray-400 leading-relaxed">
                                I'm always open to discussing advanced architectural patterns, AI integration, or high-performance frontend challenges. Reach out via the terminal or traditional protocols.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {[
                                { icon: <Mail className="text-emerald-500" />, label: "EMAIL_ENDPOINT", value: "paras@lehana.in" },
                                { icon: <Phone className="text-emerald-500" />, label: "VOICE_ENCRYPTION", value: "+91-XXXXXXXXXX" },
                                { icon: <MapPin className="text-emerald-500" />, label: "GEO_LOCATION", value: "Rajasthan, India" }
                            ].map((item, i) => (
                                <GlassCard key={i} className="flex items-center gap-6 border-white/5 hover:border-emerald-500/30 transition-colors">
                                    <div className="p-3 bg-emerald-500/10 rounded-lg">{item.icon}</div>
                                    <div>
                                        <div className="text-[10px] font-mono text-emerald-500/40 uppercase tracking-widest">{item.label}</div>
                                        <div className="text-white font-medium">{item.value}</div>
                                    </div>
                                </GlassCard>
                            ))}
                        </div>

                        <div className="flex gap-6">
                            <a href="#" className="p-4 bg-white/5 rounded-full hover:bg-emerald-500 hover:text-black transition-all duration-300">
                                <Github size={24} />
                            </a>
                            <a href="#" className="p-4 bg-white/5 rounded-full hover:bg-emerald-500 hover:text-black transition-all duration-300">
                                <Linkedin size={24} />
                            </a>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <GlassCard className="p-10 border-emerald-500/20 bg-black/40 relative overflow-hidden group">
                        {/* Animated background glow when signal connects */}
                        <motion.div
                            style={{ scale: glowScale }}
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"
                        />

                        {isSubmitted ? (
                            <div className="h-full flex flex-col items-center justify-center text-center space-y-6 py-20">
                                <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center animate-bounce">
                                    <Send className="text-black w-10 h-10" />
                                </div>
                                <h3 className="text-2xl font-bold text-white uppercase tracking-widest">Signal Transmitted</h3>
                                <p className="text-emerald-400 font-mono text-sm leading-relaxed">
                                    MESSAGE_RCVD [OK]<br />
                                    AWAITING_HANDSHAKE_RESPONSE...
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
                                <div className="grid grid-cols-1 gap-8">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-mono text-emerald-500 uppercase tracking-[0.3em]">Identity Name</label>
                                        <input
                                            required
                                            type="text"
                                            placeholder="GUEST_USER"
                                            className="w-full bg-white/5 border-b border-white/10 p-4 font-mono text-white focus:outline-none focus:border-emerald-500 transition-colors uppercase placeholder:text-gray-700"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-mono text-emerald-500 uppercase tracking-[0.3em]">Communication Channel</label>
                                        <input
                                            required
                                            type="email"
                                            placeholder="SMTP://ADDRESS"
                                            className="w-full bg-white/5 border-b border-white/10 p-4 font-mono text-white focus:outline-none focus:border-emerald-500 transition-colors placeholder:text-gray-700"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-mono text-emerald-500 uppercase tracking-[0.3em]">Payload Content</label>
                                        <textarea
                                            required
                                            rows={4}
                                            placeholder="WRITE_YOUR_MISSION_BRIEF_HERE..."
                                            className="w-full bg-white/5 border border-white/10 p-4 font-mono text-white focus:outline-none focus:border-emerald-500 transition-colors placeholder:text-gray-700"
                                        ></textarea>
                                    </div>
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    type="submit"
                                    className="w-full py-5 bg-emerald-500 text-black font-black uppercase tracking-[0.5em] shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:shadow-[0_0_50px_rgba(16,185,129,0.5)] transition-all"
                                >
                                    Transmit Signal
                                </motion.button>
                            </form>
                        )}
                    </GlassCard>
                </motion.div>
            </section>

            {/* Bottom Footer Info */}
            <div className="py-20 text-center opacity-30 font-mono text-[9px] tracking-[1em] uppercase">
                Secure Transmission Line: ACTIVE // Encrypted Channel 0x1A2B3D
            </div>
        </div>
    );
}
